import IconButton from '@/components/Buttons/IconButton';
import { BoxInnerImage, ImageAssetCard } from '@/components/Cards/ImageAssetCard';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { useGetPresentersList } from '@/hooks';
import Heart from '@/images/Heart.svg';
import HeartFilled from '@/images/HeartFilled.svg';
import { isGreenScreenAvatar, isUserAvatar, isUserClipAvatar, useAddAvatarToFavourites } from '@/pages/avatar';
import { useAgentIsV2 } from '@/pages/avatar/hooks/useAgentIsV2';
import { useAnalytics } from '@/services/analytics';
import { useFlag } from '@/services/featureFlags';
import theme from '@/theme';
import { AppRoutes } from '@/utils/types';
import { isDvpPresenter, Presenter, PresenterUsecase, VideoType } from '@d-id/types';
import styled from '@emotion/styled';
import CircularProgress from '@mui/material/CircularProgress';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { DEFAULT_AVATARS_GROUPING } from '../../constants';
import { groupByLongestPrefix } from '../../utils';
import TooltipBackgroundButton from './common/backgroundButton/TooltipBackgroundButton';
import { DvpSubSelection } from './DvpSubSelection';
import { isNewPresenter } from './utils/IsNewPresenter';

export const FavouriteButton = styled(IconButton)<{ active?: boolean }>`
    color: ${({ theme }) => theme.colors.black[900]} !important;
    background-color: #fff !important;
    border: 1px solid ${({ theme }) => theme.colors.black[300]};
    border-radius: 8px;
    width: 24px;
    height: 24px;
    border-radius: 4px !important;

    display: none;
    position: absolute;
    z-index: 1;
    top: 8px;
    left: 8px;

    :focus {
        outline: none;
    }

    :hover {
        background-color: ${({ theme }) => theme.colors.black[200]} !important;
    }

    color: ${theme.colors.secondary[900]} !important;

    ${({ active }) =>
        active &&
        `
      color: ${theme.colors.primary[900]} !important;
    `}
`;

const StyledTooltipBackgroundButton = styled(TooltipBackgroundButton)<{ canRemove: boolean }>`
    display: none;
    right: ${({ canRemove }) => (canRemove ? '36px' : '8px')};
`;

const StyledImageAssetCard = styled(ImageAssetCard)<{ selected: boolean }>`
    aspect-ratio: 16/9;
    background: transparent !important;
    padding: 0;
    overflow: hidden;

    border: 1px solid ${theme.colors.black[300]};

    ${({ selected, theme }) =>
        selected &&
        `
        border: 1px solid ${theme.colors.primary.main} !important;
    `}

    :hover {
        border: 1px solid ${theme.colors.secondary[900]};
    }

    :hover ${FavouriteButton} {
        display: inline-flex;
    }

    :hover ${StyledTooltipBackgroundButton} {
        display: inline-flex;
    }

    :hover ${BoxInnerImage} {
        &::after {
            background: #fff;
            opacity: 0.15;
            width: 100%;
            height: 100%;
            position: absolute;
            border-radius: 8px;
            top: 0;
            left: 0;
            content: '';
        }
    }

    .image-asset-card-image {
        border-radius: 0;
        background-size: contain;
    }
`;

const preparePresenterAndAlternatives = (presenter: Presenter, alternatives: string[], presenters: Presenter[]) => {
    if (!alternatives.length) return [];

    const result: Presenter[] = [presenter];

    alternatives.forEach(alternativeId => {
        const foundAlternative = presenters.find(p => p.id === alternativeId);
        if (foundAlternative) {
            result.push(foundAlternative);
        }
    });

    return result;
};

interface Props {
    agentId?: string;
    presenter: Presenter;
    onClick: (item: Presenter) => void;
    onRemove: (item: Presenter) => void;
    setIsBackgroundsModalOpen: (isOpen: boolean) => void;
    isBackgroundProcessing?: boolean;
    selectedPresenter: Presenter | undefined;
    showExpressiveTag?: boolean;
}

export const PresenterAssetCard = ({
    agentId,
    presenter,
    onClick,
    onRemove,
    setIsBackgroundsModalOpen,
    isBackgroundProcessing = false,
    selectedPresenter,
    showExpressiveTag = false,
}: Props) => {
    const { isMutating, isLoading, isFavourite, toggle } = useAddAvatarToFavourites(presenter.id);
    const migratableAvatars: string[] = useFlag('migrated_avatars', []);
    const newAvatarsGrouping = useFlag('new_avatars_grouping', false);
    const avatarsGrouping = useFlag('avatars_grouping', DEFAULT_AVATARS_GROUPING as Record<string, string[]>);
    const isV2 = useAgentIsV2(agentId);
    const mixpanel = useAnalytics();
    const { pathname } = useLocation();
    const isCampaignsPage = pathname?.includes(AppRoutes.Campaigns);
    const { data: presenters } = useGetPresentersList({ usecase: PresenterUsecase.Agent });

    const handleToggle = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        await toggle(e);
    };

    const isNew = useMemo(() => isNewPresenter(presenter), [presenter]);
    const canRemove = presenter.type !== VideoType.CLIP && !presenter.isDefault;

    const openBackgroundsModal = (e: React.MouseEvent<HTMLButtonElement>, presenter: Presenter) => {
        e.stopPropagation();

        onClick(presenter);

        mixpanel?.track('agent-change-background', {
            event: 'click',
            type: presenter.type,
        });
        setIsBackgroundsModalOpen(true);
    };

    const handleOnThumbnailClick = (e: React.MouseEvent<HTMLDivElement>, presenter: Presenter) => {
        e.stopPropagation();

        mixpanel?.track('agent-update-background', {
            event: 'click',
            agentType: presenter?.name?.includes('Green Screen') ? 'white' : 'default',
            presenterType: 'clip',
        });
        onClick(presenter);
    };

    const dvpPresenters = useMemo((): Presenter[] => {
        if (!presenters || !presenter?.name) return [presenter];

        if (newAvatarsGrouping) {
            return preparePresenterAndAlternatives(presenter, avatarsGrouping[presenter.id] || [], presenters);
        }

        const pluses = presenters.filter(
            p => p.id.startsWith('v2_') && !p.id.startsWith('v2_private') && migratableAvatars.some(m => p.id.includes(m))
        );
        const group = Object.values(groupByLongestPrefix(pluses, p => p.id))
            .find(g => g.find(p => p.id === presenter.id))
            ?.reverse();

        if (group) {
            const videoBg = group.find(p => p.poster?.includes('video_bg'));
            const greenScreen = group.find(p => p.poster?.includes('green_screen'));

            const rest = group.filter(p => p !== videoBg && p !== greenScreen);

            const ordered = [];
            if (videoBg) {
                ordered.push(videoBg);
            }

            ordered.push(...rest);

            if (greenScreen) {
                ordered.push(greenScreen);
            }

            return ordered;
        }

        return [presenter];
    }, [migratableAvatars, presenter, presenters]);

    const showBlur =
        (isUserAvatar(presenter) || isUserClipAvatar(presenter)) && !isDvpPresenter(presenter) && !isGreenScreenAvatar(presenter);

    const isSelected = presenter.id === selectedPresenter?.id || dvpPresenters.some(p => p.id === selectedPresenter?.id);

    return (
        <StyledImageAssetCard
            showBlurredBackground={showBlur}
            selected={isSelected}
            key={presenter.order}
            item={presenter as Presenter}
            variant="lite-border"
            presenterId={presenter.id}
            type={presenter.type}
            src={presenter.thumbnail || presenter.poster}
            onRemove={canRemove ? async () => onRemove(presenter) : undefined}
            onClick={() => onClick(presenter)}
            isNew={isNew}
            looks={dvpPresenters.length}
            isAiGenerated={presenter?.is_aiGenerated}
            showExpressiveTag={showExpressiveTag}>
            <FavouriteButton ariaLabel="favourite" active={isFavourite} onClick={handleToggle} disabled={isLoading || isMutating}>
                {isLoading ? (
                    <CircularProgress size={16} style={{ minWidth: '16px' }} />
                ) : (
                    <MaskedIcon src={isFavourite ? HeartFilled : Heart} size="16px" />
                )}
            </FavouriteButton>

            {isV2 && !isCampaignsPage && (
                <>
                    {isDvpPresenter(presenter) && !!dvpPresenters.length ? (
                        <StyledTooltipBackgroundButton
                            canRemove={canRemove}
                            tooltipTitle={
                                <DvpSubSelection
                                    presenters={dvpPresenters}
                                    onThumbnailClick={handleOnThumbnailClick}
                                    selectedPresenter={selectedPresenter}
                                    openBackgroundsModal={openBackgroundsModal}
                                />
                            }
                            tooltipStyles={{
                                borderRadius: '8px',
                                boxShadow: `2px 2px 16px -1.2px rgba(0, 0, 0, 0.12)`,
                            }}
                            isProcessing={isBackgroundProcessing}
                            disabled={isBackgroundProcessing}
                            customBackgroundColor={theme.colors.white}
                            arrow={true}
                        />
                    ) : presenter?.type === VideoType.TALK || (isDvpPresenter(presenter) && presenter.is_greenscreen) ? (
                        <StyledTooltipBackgroundButton
                            canRemove={canRemove}
                            tooltipTitle="Change Background"
                            isProcessing={isBackgroundProcessing}
                            onClick={e => openBackgroundsModal(e, presenter)}
                            disabled={isBackgroundProcessing}
                        />
                    ) : null}
                </>
            )}
        </StyledImageAssetCard>
    );
};
